import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { KpiCards } from '@/components/sections/kpi-cards'
import { RevenueTimeChart } from '@/components/charts/revenue-time-chart'
import { TopItemsChart } from '@/components/charts/top-items-chart'
import { DebtTab } from '@/components/reports/debt-tab'
import { GeographyTab } from '@/components/reports/geography-tab'
import { InventoryTab } from '@/components/reports/inventory-tab'
import { StaffTab } from '@/components/reports/staff-tab'
import { DeliveryTab } from '@/components/reports/delivery-tab'
import {
    useCompareRevenueSummary,
    useDebtSummary,
    useOrdersSummary,
    useRevenueByCustomer,
    useRevenueByProduct,
    useRevenueByTime,
} from '@/hooks/use-report-query'
import { reportApi } from '@/lib/api/report-api'
import { getDefaultDateRange, getPresetRange } from '@/lib/date-presets'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/reports/')({
    // `tab` lets other pages (e.g. the manager dashboard overview blocks) deep-link
    // straight to a specific report tab instead of always landing on revenue.
    validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
        tab: typeof search.tab === 'string' ? search.tab : undefined,
    }),
    component: ReportsPage,
})

function RevenueTabContent() {
    const defaults = getDefaultDateRange()
    const [dateFrom, setDateFrom] = useState(defaults.from)
    const [dateTo, setDateTo] = useState(defaults.to)
    const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day')

    const params = { date_from: dateFrom, date_to: dateTo }

    const { data: compare } = useCompareRevenueSummary(params)
    const summary = compare?.current ?? null
    const deltas = compare?.deltas ?? null
    const { data: byTime = [] } = useRevenueByTime({
        ...params,
        group_by: groupBy,
    })
    const { data: byProduct = [] } = useRevenueByProduct({
        ...params,
        limit: 10,
    })
    const { data: byCustomer = [] } = useRevenueByCustomer({
        ...params,
        limit: 10,
    })
    const { data: ordersSummary } = useOrdersSummary(params)
    const { data: debtSummary } = useDebtSummary(params)

    const handleExport = () => {
        const url = reportApi.exportExcelUrl({
            ...params,
            type: 'revenue_summary',
        })
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1.5">
                    {[
                        { key: 'today', label: m.report_preset_today() },
                        { key: '7d', label: m.report_preset_7d() },
                        { key: '30d', label: m.report_preset_30d() },
                        { key: 'month', label: m.report_preset_month() },
                        { key: 'quarter', label: m.report_preset_quarter() },
                        { key: 'year', label: m.report_preset_year() },
                    ].map((p) => (
                        <Button
                            key={p.key}
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                const r = getPresetRange(p.key)
                                setDateFrom(r.from)
                                setDateTo(r.to)
                            }}
                        >
                            {p.label}
                        </Button>
                    ))}
                </div>
                <Button size="sm" variant="outline" onClick={handleExport}>
                    {m.report_export_excel()}
                </Button>
            </div>

            {/* Date range filter */}
            <div className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">
                        {m.report_from_date()}
                    </label>
                    <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="w-40"
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">
                        {m.report_to_date()}
                    </label>
                    <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="w-40"
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">
                        {m.report_group_by()}
                    </label>
                    <Select
                        value={groupBy}
                        onValueChange={(v) => setGroupBy(v as typeof groupBy)}
                    >
                        <SelectTrigger className="w-32">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="day">
                                {m.report_group_day()}
                            </SelectItem>
                            <SelectItem value="week">
                                {m.report_group_week()}
                            </SelectItem>
                            <SelectItem value="month">
                                {m.report_group_month()}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* KPI Cards */}
            {summary && (
                <KpiCards
                    totalRevenue={summary.total_revenue}
                    totalOrders={summary.total_orders}
                    averageOrderValue={summary.average_order_value}
                    unpaidAmount={summary.unpaid_amount}
                    deltas={deltas ?? undefined}
                />
            )}

            {/* Debt summary row */}
            {debtSummary && (
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground mb-3">
                        {m.report_debt_summary()}
                    </h2>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        {[
                            {
                                label: m.report_total_unpaid(),
                                value: formatVnd(debtSummary.total_unpaid),
                            },
                            {
                                label: m.report_total_partial(),
                                value: formatVnd(debtSummary.total_partial),
                            },
                            {
                                label: m.report_total_overdue(),
                                value: formatVnd(debtSummary.total_overdue),
                                highlight: debtSummary.total_overdue > 0,
                            },
                            {
                                label: m.report_debt_customers(),
                                value: debtSummary.customer_count.toString(),
                            },
                        ].map((item) => (
                            <Card key={item.label}>
                                <CardContent className="pt-4 pb-4">
                                    <div
                                        className={`text-2xl font-bold ${
                                            'highlight' in item &&
                                            item.highlight
                                                ? 'text-red-500'
                                                : ''
                                        }`}
                                    >
                                        {item.value}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        {item.label}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* Orders summary row */}
            {ordersSummary && (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                    {[
                        {
                            label: m.report_orders_total(),
                            value: ordersSummary.total_orders,
                        },
                        {
                            label: m.report_orders_new(),
                            value: ordersSummary.by_status?.['new'] ?? 0,
                        },
                        {
                            label: m.report_orders_confirmed(),
                            value: ordersSummary.by_status?.['confirmed'] ?? 0,
                        },
                        {
                            label: m.report_orders_delivered(),
                            value: ordersSummary.by_status?.['delivered'] ?? 0,
                        },
                        {
                            label: m.report_orders_cancelled(),
                            value: ordersSummary.by_status?.['cancelled'] ?? 0,
                        },
                        {
                            label: m.report_orders_returned(),
                            value: ordersSummary.by_status?.['returned'] ?? 0,
                        },
                    ].map((item) => (
                        <Card key={item.label}>
                            <CardContent className="pt-4 pb-4">
                                <div className="text-2xl font-bold">
                                    {item.value}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {item.label}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Revenue by time chart - full width */}
            <RevenueTimeChart data={byTime} />

            {/* Top items charts */}
            <div className="grid gap-6 lg:grid-cols-2">
                <TopItemsChart
                    title={m.report_top_products_title()}
                    data={byProduct.map((p) => ({
                        name: p.product_name,
                        revenue: p.revenue,
                    }))}
                />
                <TopItemsChart
                    title={m.report_top_customers_title()}
                    data={byCustomer.map((c) => ({
                        name: c.customer_name,
                        revenue: c.revenue,
                    }))}
                    color="hsl(var(--chart-2, 160 60% 45%))"
                />
            </div>
        </div>
    )
}

function ReportsPage() {
    const { tab } = Route.useSearch()
    return (
        <div className="space-y-4">
            <h1 className="text-xl font-semibold">{m.report_title_main()}</h1>

            <Tabs defaultValue={tab ?? 'revenue'}>
                <TabsList>
                    <TabsTrigger value="revenue">
                        {m.report_tab_revenue()}
                    </TabsTrigger>
                    <TabsTrigger value="geography">
                        {m.report_tab_geography()}
                    </TabsTrigger>
                    <TabsTrigger value="inventory">
                        {m.report_tab_inventory()}
                    </TabsTrigger>
                    <TabsTrigger value="debts">
                        {m.report_tab_debts()}
                    </TabsTrigger>
                    <TabsTrigger value="staff">
                        {m.report_tab_staff()}
                    </TabsTrigger>
                    <TabsTrigger value="delivery">
                        {m.report_tab_delivery()}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="revenue">
                    <RevenueTabContent />
                </TabsContent>

                <TabsContent value="geography">
                    <GeographyTab />
                </TabsContent>

                <TabsContent value="inventory">
                    <InventoryTab />
                </TabsContent>

                <TabsContent value="debts">
                    <DebtTab />
                </TabsContent>

                <TabsContent value="staff">
                    <StaffTab />
                </TabsContent>

                <TabsContent value="delivery">
                    <DeliveryTab />
                </TabsContent>
            </Tabs>
        </div>
    )
}
